'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Clock, ArrowRight } from 'lucide-react';

interface ViewedProduct {
  slug: string;
  name: string;
  vendorName: string;
  iconUrl?: string;
  viewedAt: number;
}

const STORAGE_KEY = 'inventive_recently_viewed';
const MAX_ITEMS = 10;

// Utility functions for managing recently viewed products
export function getRecentlyViewed(): ViewedProduct[] {
  if (typeof window === 'undefined') return [];
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const items = JSON.parse(stored) as ViewedProduct[];
    // Filter out items older than 30 days
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return items.filter((item) => item.viewedAt > thirtyDaysAgo);
  } catch {
    return [];
  }
}

export function addToRecentlyViewed(product: Omit<ViewedProduct, 'viewedAt'>): void {
  if (typeof window === 'undefined') return;
  try {
    const items = getRecentlyViewed();
    // Remove existing entry for this product
    const filtered = items.filter((item) => item.slug !== product.slug);
    // Add new entry at the beginning
    const updated = [{ ...product, viewedAt: Date.now() }, ...filtered].slice(0, MAX_ITEMS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    // Dispatch event for other components to update
    window.dispatchEvent(new CustomEvent('recentlyViewedUpdated'));
  } catch {
    // Ignore storage errors
  }
}

export function clearRecentlyViewed(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent('recentlyViewedUpdated'));
  } catch {
    // Ignore storage errors
  }
}

interface RecentlyViewedProps {
  currentSlug?: string; // Exclude current product from display
  maxItems?: number;
  className?: string;
}

export default function RecentlyViewed({
  currentSlug,
  maxItems = 4,
  className = '',
}: RecentlyViewedProps) {
  const [items, setItems] = useState<ViewedProduct[]>([]);

  useEffect(() => {
    const loadItems = () => {
      const viewed = getRecentlyViewed();
      const filtered = currentSlug
        ? viewed.filter((item) => item.slug !== currentSlug)
        : viewed;
      setItems(filtered.slice(0, maxItems));
    };

    loadItems();

    // Listen for updates from other tabs/components
    const handleUpdate = () => loadItems();
    window.addEventListener('recentlyViewedUpdated', handleUpdate);
    window.addEventListener('storage', handleUpdate);

    return () => {
      window.removeEventListener('recentlyViewedUpdated', handleUpdate);
      window.removeEventListener('storage', handleUpdate);
    };
  }, [currentSlug, maxItems]);

  if (items.length === 0) return null;

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Recently Viewed</h3>
        </div>
        <button
          onClick={clearRecentlyViewed}
          className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-400"
          title="Clear history"
        >
          Clear
        </button>
      </div>
      <div className="space-y-3">
        {items.map((product) => (
          <Link
            key={product.slug}
            href={`/software/${product.slug}`}
            className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group"
          >
            {product.iconUrl ? (
              <img
                src={product.iconUrl}
                alt={product.name}
                className="w-10 h-10 rounded-lg object-contain bg-gray-50 dark:bg-gray-800"
              />
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-sm font-bold text-white">
                  {product.name.charAt(0)}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500 dark:text-gray-400">{product.vendorName}</p>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                {product.name}
              </h4>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
          </Link>
        ))}
      </div>
    </div>
  );
}

// Compact version for sidebars
export function RecentlyViewedCompact({
  currentSlug,
  maxItems = 3,
}: Omit<RecentlyViewedProps, 'className'>) {
  const [items, setItems] = useState<ViewedProduct[]>([]);

  useEffect(() => {
    const loadItems = () => {
      const viewed = getRecentlyViewed();
      const filtered = currentSlug
        ? viewed.filter((item) => item.slug !== currentSlug)
        : viewed;
      setItems(filtered.slice(0, maxItems));
    };

    loadItems();

    const handleUpdate = () => loadItems();
    window.addEventListener('recentlyViewedUpdated', handleUpdate);
    window.addEventListener('storage', handleUpdate);

    return () => {
      window.removeEventListener('recentlyViewedUpdated', handleUpdate);
      window.removeEventListener('storage', handleUpdate);
    };
  }, [currentSlug, maxItems]);

  if (items.length === 0) return null;

  return (
    <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Recently Viewed</span>
      </div>
      <div className="space-y-2">
        {items.map((product) => (
          <Link
            key={product.slug}
            href={`/software/${product.slug}`}
            className="block text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 truncate"
          >
            {product.name}
          </Link>
        ))}
      </div>
    </div>
  );
}
